import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { ActionButton as StatusActionButton } from '@shared/components/status-filter/status-filter.component';
import {
  FormField,
  TableSection,
  SummarySection,
  TableAction,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  ORDER_INTERNAL_PAYMENT_STATUS_LIST,
  ORDER_INTERNAL_PAYMENT_METHOD_LIST,
} from '@app/shared/utils/constants.util';
import { vietnamesePhoneNumberValidator } from '@app/shared/utils/phone-validator.util';
import type { PackageFulfillmentMode } from '../../order/internal/internal.config';

export const O2O_TAKING_DELIVERY_WAY = {
  pickup: 'STORE_PICKUP',
  delivery: 'SHIP_TO_ADDRESS',
} as const;

export const O2O_CREATE_TAKING_DELIVERY_WAY_OPTIONS = [
  {
    label: 'constant.status.pickup',
    value: O2O_TAKING_DELIVERY_WAY.pickup,
  },
  {
    label: 'constant.status.delivery',
    value: O2O_TAKING_DELIVERY_WAY.delivery,
  },
];

// ============================================================================
// INTERFACES
// ============================================================================

export interface O2OInternalConfig {
  searchConfig: FormFieldConfig[];
  statusOptions: Array<{
    key: string;
    label: string;
    active?: boolean;
  }>;
  primaryButton: StatusActionButton;
  tableColumns: TableColumn[];
}

export interface O2OCreateConfig {
  headerActions: ActionButton[];
  tableSections: TableSection[];
  summarySection: SummarySection;
  formFields: FormField[];
}

export interface OrderProduct {
  id: number;
  serialNo: number;
  productName: string;
  productCode: string;
  skuName: string;
  skuCode: string;
  quantity: number;
  isGift: boolean;
  thirdPartyPrice: number;
  thirdPartyTotal: number;
  internalPrice: number;
  internalTotal: number;
  costPrice: number;
  costTotal: number;
  setFlag?: number;
  isPackage?: boolean;
  fulfillmentMode?: PackageFulfillmentMode;
  packageChildren?: PackageSkuChild[];
}

/** Display-only package component shown below its package SKU. */
export interface PackageSkuChild {
  skuId?: number;
  spuCode: string;
  spuName: string;
  skuCode: string;
  skuName: string;
  quantityPerPackage: number;
  quantity: number;
}

// ============================================================================
// O2O INTERNAL CONFIGURATION
// ============================================================================

// Search Form Configuration
export const O2O_INTERNAL_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'orderNumber',
    label: 'o2o.internal.search.fields.order_number',
    type: 'text',
    placeholder: 'o2o.internal.search.placeholders.enter_order_number',
  },
  {
    key: 'buyerNickname',
    label: 'o2o.internal.search.fields.buyer_nickname',
    type: 'text',
    placeholder: 'o2o.internal.search.placeholders.enter_buyer_nickname',
  },
  {
    key: 'receiverName',
    label: 'o2o.internal.search.fields.receiver_name',
    type: 'text',
    placeholder: 'o2o.internal.search.placeholders.enter_receiver_name',
  },
  {
    key: 'mobile',
    label: 'o2o.internal.search.fields.receiver_phone',
    type: 'text',
    placeholder: 'o2o.internal.search.placeholders.enter_receiver_phone',
  },
  {
    key: 'salesStore',
    label: 'o2o.internal.search.fields.sales_store',
    type: 'multiselect',
    options: [],
    placeholder: 'o2o.internal.search.placeholders.select_sales_store',
  },
  // {
  //   key: 'payStatus',
  //   label: 'o2o.internal.search.fields.payment_status',
  //   type: 'select',
  //   options: ORDER_INTERNAL_PAYMENT_STATUS_LIST,
  //   placeholder: 'o2o.internal.search.placeholders.select_payment_status',
  // },
  {
    key: 'isGift',
    label: 'o2o.internal.search.fields.gift_status',
    type: 'select',
    options: [
      { label: 'o2o.internal.search.options.yes', value: 'yes' },
      { label: 'o2o.internal.search.options.no', value: 'no' },
    ],
    placeholder: 'o2o.internal.search.placeholders.select_gift_status',
  },
  {
    key: 'stockHoldTimeoutOnly',
    label: 'stock_hold_timeout.order.filter_timeout_only',
    type: 'select',
    width: '280px',
    options: [
      { label: 'common.table.status.yes', value: 'yes' },
      { label: 'common.table.status.no', value: 'no' },
    ],
    placeholder: 'stock_hold_timeout.order.filter_timeout_only',
  },
];

// Status Filter Options
export const O2O_INTERNAL_STATUS_OPTIONS = [
  { key: 'all', label: 'o2o.internal.status.all', active: true },
  { key: 'incomplete', label: 'o2o.internal.status.incomplete' },
  { key: 'completed', label: 'o2o.internal.status.completed' },
  { key: 'closed', label: 'o2o.internal.status.closed' },
  { key: 'exception', label: 'o2o.internal.status.exception' },
];

// Action Buttons
export const O2O_INTERNAL_PRIMARY_BUTTON: StatusActionButton = {
  label: 'o2o.internal.actions.create_order',
  severity: 'info',
  outlined: false,
  disabled: false,
  show: true,
  icon: 'add',
};

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const O2O_INTERNAL_DEFAULT_COLUMNS = {
  THIRD_PARTY_ORDER_NUMBER: 'thirdPartyOrderNumber',
  SALES_STORE: 'salesStore',
  INTERNAL_ORDER_NUMBER: 'internalOrderNumber',
  BUYER_NICKNAME: 'buyerNickname',
  PICKUP_METHOD: 'pickupMethod',
  PAY_STATUS: 'payStatus',
  ORDER_STATUS: 'orderStatus',
  DELIVERY_STATUS: 'deliveryStatus',
  RECEIVING_STATUS: 'receivingStatus',
  TOTAL_PRODUCT_AMOUNT: 'totalProductAmount',
  TOTAL_PRODUCT_COST: 'totalProductCost',
  BUYER_PAYABLE: 'buyerPayable',
  THIRD_PARTY_ORDER_TIME: 'thirdPartyOrderTime',
  INTERNAL_CREATION_TIME: 'internalCreationTime',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const O2O_INTERNAL_FLEXIBLE_COLUMNS = {
  SALES_CHANNEL: 'salesChannel',
  QUANTITY: 'quantity',
  BUYER_NOTES: 'buyerNotes',
  PAY_TYPE: 'payType',
  RECEIVER_NAME: 'receiverName',
  RECEIVER_PHONE: 'receiverPhone',
  RECEIVER_ADDRESS: 'receiverAddress',
  PAYMENT_TIME: 'paymentTime',
  GIFT_STATUS: 'giftStatus',
  STOCK_HOLD_CATEGORY: 'stockHoldCategory',
  STOCK_HOLD_EXPIRY_TIME: 'stockHoldExpiryTime',
  STOCK_HOLD_TIMEOUT_MINUTES: 'stockHoldTimeoutMinutes',
  FAILURE_REASON_DESCRIPTION: 'failureReasonDescription',
} as const;

/**
 * Action column property name
 */
export const O2O_INTERNAL_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const O2O_INTERNAL_DEFAULT_COLUMNS_ORDER: string[] = [
  O2O_INTERNAL_DEFAULT_COLUMNS.THIRD_PARTY_ORDER_NUMBER,
  O2O_INTERNAL_DEFAULT_COLUMNS.SALES_STORE,
  O2O_INTERNAL_DEFAULT_COLUMNS.INTERNAL_ORDER_NUMBER,
  O2O_INTERNAL_DEFAULT_COLUMNS.BUYER_NICKNAME,
  O2O_INTERNAL_DEFAULT_COLUMNS.PICKUP_METHOD,
  O2O_INTERNAL_DEFAULT_COLUMNS.ORDER_STATUS,
  'fulfillmentStatus',
  O2O_INTERNAL_DEFAULT_COLUMNS.PAY_STATUS,
  O2O_INTERNAL_DEFAULT_COLUMNS.TOTAL_PRODUCT_AMOUNT,
  O2O_INTERNAL_DEFAULT_COLUMNS.TOTAL_PRODUCT_COST,
  O2O_INTERNAL_DEFAULT_COLUMNS.BUYER_PAYABLE,
  O2O_INTERNAL_DEFAULT_COLUMNS.THIRD_PARTY_ORDER_TIME,
  O2O_INTERNAL_DEFAULT_COLUMNS.INTERNAL_CREATION_TIME,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const O2O_INTERNAL_FLEXIBLE_COLUMNS_ORDER: string[] = [
  O2O_INTERNAL_DEFAULT_COLUMNS.DELIVERY_STATUS,
  O2O_INTERNAL_DEFAULT_COLUMNS.RECEIVING_STATUS,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.SALES_CHANNEL,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.QUANTITY,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.BUYER_NOTES,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.PAY_TYPE,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.RECEIVER_NAME,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.RECEIVER_PHONE,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.RECEIVER_ADDRESS,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.PAYMENT_TIME,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.GIFT_STATUS,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.STOCK_HOLD_CATEGORY,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.STOCK_HOLD_EXPIRY_TIME,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.STOCK_HOLD_TIMEOUT_MINUTES,
  O2O_INTERNAL_FLEXIBLE_COLUMNS.FAILURE_REASON_DESCRIPTION,
];

// Table Columns
export const O2O_INTERNAL_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'o2o.internal.table.third_party_order_number',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.THIRD_PARTY_ORDER_NUMBER,
    width: 120,
  },
  {
    label: 'o2o.internal.table.sales_store',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.SALES_STORE,
    width: 150,
  },
  {
    label: 'o2o.internal.table.sales_channel',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.SALES_CHANNEL,
    width: 120,
  },
  {
    label: 'o2o.internal.table.internal_order_number',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.INTERNAL_ORDER_NUMBER,
    width: 150,
  },
  {
    label: 'o2o.internal.table.quantity',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.QUANTITY,
    width: 120,
  },
  {
    label: 'o2o.internal.table.buyer_nickname',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.BUYER_NICKNAME,
    width: 120,
  },
  {
    label: 'o2o.internal.table.buyer_notes',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.BUYER_NOTES,
    width: 150,
  },
  {
    label: 'o2o.internal.table.pickup_method',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.PICKUP_METHOD,
    width: 120,
    type: 'custom',
    template: 'typeTemplate',
  },
  {
    label: 'o2o.internal.table.payment_method',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.PAY_TYPE,
    width: 120,
    type: 'custom',
    template: 'typeTemplate',
  },
  {
    label: 'o2o.internal.table.receiver_name',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.RECEIVER_NAME,
    width: 120,
  },
  {
    label: 'o2o.internal.table.receiver_phone',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.RECEIVER_PHONE,
    width: 150,
  },
  {
    label: 'o2o.internal.table.receiver_address',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.RECEIVER_ADDRESS,
    width: 200,
  },
  {
    label: 'o2o.internal.table.order_status',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.ORDER_STATUS,
    width: 160,
    type: 'custom',
    template: 'statusTemplate',
  },
  {
    label: 'order.table.fulfillment_status',
    prop: 'fulfillmentStatus',
    width: 160,
    type: 'custom',
    template: 'statusTemplate',
  },
  {
    label: 'o2o.internal.table.payment_status',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.PAY_STATUS,
    width: 120,
    type: 'custom',
    template: 'statusTemplate',
  },
  {
    label: 'o2o.internal.table.delivery_status',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.DELIVERY_STATUS,
    width: 120,
    type: 'custom',
    template: 'statusTemplate',
  },
  {
    label: 'o2o.internal.table.receipt_status',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.RECEIVING_STATUS,
    width: 120,
    type: 'custom',
    template: 'statusTemplate',
  },
  {
    label: 'o2o.internal.table.total_product_amount',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.TOTAL_PRODUCT_AMOUNT,
    width: 150,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'o2o.internal.table.total_product_cost',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.TOTAL_PRODUCT_COST,
    width: 150,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'o2o.internal.table.buyer_payable',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.BUYER_PAYABLE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'o2o.internal.table.third_party_order_time',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.THIRD_PARTY_ORDER_TIME,
    width: 150,
    type: 'custom',
    template: 'operationTimeTemplate',
  },
  {
    label: 'o2o.internal.table.payment_time',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.PAYMENT_TIME,
    width: 150,
    type: 'custom',
    template: 'operationTimeTemplate',
  },
  {
    label: 'o2o.internal.table.internal_creation_time',
    prop: O2O_INTERNAL_DEFAULT_COLUMNS.INTERNAL_CREATION_TIME,
    width: 150,
    type: 'custom',
    template: 'operationTimeTemplate',
  },
  {
    label: 'o2o.internal.table.gift_status',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.GIFT_STATUS,
    width: 100,
    type: 'custom',
  },
  {
    label: 'stock_hold_timeout.order.hold_category',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.STOCK_HOLD_CATEGORY,
    width: 160,
    type: 'custom',
    template: 'stockHoldCategoryTemplate',
  },
  {
    label: 'stock_hold_timeout.order.expiry_time',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.STOCK_HOLD_EXPIRY_TIME,
    width: 160,
    type: 'custom',
    template: 'stockHoldExpiryTemplate',
  },
  {
    label: 'stock_hold_timeout.order.timeout',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.STOCK_HOLD_TIMEOUT_MINUTES,
    width: 120,
    type: 'custom',
    template: 'stockHoldTimeoutTemplate',
  },
  {
    label: 'stock_hold_timeout.order.failure_reason',
    prop: O2O_INTERNAL_FLEXIBLE_COLUMNS.FAILURE_REASON_DESCRIPTION,
    width: 160,
    type: 'custom',
    template: 'failureReasonTemplate',
  },
  {
    label: 'o2o.internal.table.action',
    prop: O2O_INTERNAL_ACTION_COLUMN.ACTIONS,
    width: 200,
    type: 'actions',
  },
];

// Main Internal Configuration
export const O2O_INTERNAL_CONFIG: O2OInternalConfig = {
  searchConfig: O2O_INTERNAL_SEARCH_CONFIG,
  statusOptions: O2O_INTERNAL_STATUS_OPTIONS,
  primaryButton: O2O_INTERNAL_PRIMARY_BUTTON,
  tableColumns: O2O_INTERNAL_TABLE_COLUMNS,
};

// ============================================================================
// O2O CREATE CONFIGURATION
// ============================================================================

// Header Actions
export const O2O_CREATE_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'o2o.create.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
  { label: 'o2o.create.actions.save', action: 'save', severity: 'info' },
];

// Table Sections
export const O2O_CREATE_TABLE_SECTIONS: TableSection[] = [
  {
    title: 'o2o.create.table.product_information',
    show: true,
    columns: [
      { prop: 'spuName', label: 'o2o.create.table.product_name', width: 100 },
      { prop: 'spuCode', label: 'o2o.create.table.product_code', width: 120 },
      { prop: 'skuName', label: 'o2o.create.table.sku_name', width: 150 },
      { prop: 'skuCode', label: 'o2o.create.table.sku_code', width: 120 },
      {
        prop: 'quantity',
        label: 'o2o.create.table.quantity',
        width: 150,
        align: 'center',
        type: 'inputNumberHorizontal',
        useGrouping: true,
        min: 1,
        step: 1,
      },
      {
        prop: 'giftFlag',
        label: 'o2o.create.table.gift',
        width: 60,
        type: 'custom',
      },
      //   { prop: 'thirdPartyPrice', label: 'o2o.create.table.platform_price', width: 120 },
      //   { prop: 'thirdPartyTotal', label: 'o2o.create.table.platform_total', width: 120 },
      //   { prop: 'internalPrice', label: 'o2o.create.table.internal_price', width: 120 },
      //   { prop: 'internalTotal', label: 'o2o.create.table.internal_total', width: 120 },
      {
        prop: 'costPrice',
        label: 'o2o.create.table.cost_price',
        width: 100,
        align: 'right',
        type: 'custom',
        template: 'currencyTemplate',
      },
      {
        prop: 'costTotal',
        label: 'o2o.create.table.cost_total',
        width: 100,
        align: 'right',
        type: 'custom',
        template: 'currencyTemplate',
      },
    ],
    data: [],
    loading: false,
    hasCheckbox: true,
    selection: [],
    actions: [
      {
        label: 'o2o.create.table.actions.add_product',
        action: 'addProduct',
        severity: 'info',
        outlined: true,
      },
      {
        label: 'o2o.create.table.actions.delete_selected',
        action: 'deleteSelected',
        severity: 'danger',
        disabled: true,
        outlined: true,
      },
      //   { label: 'o2o.create.table.actions.add_package', action: 'addPackage', severity: 'secondary', outlined: true }
    ] as TableAction[],
  },
];

// Summary Section
export const O2O_CREATE_SUMMARY_SECTION: SummarySection = {
  title: 'o2o.create.summary.title',
  show: true,
  items: [
    {
      label: 'o2o.create.summary.selected_items',
      value: 0,
      formatter: 'number',
    },
    {
      label: 'o2o.create.summary.goods_total_amount',
      value: 0,
      formatter: 'currency',
      currency: 'VND',
    },
    {
      label: 'o2o.create.summary.total_shipping',
      value: 0,
      formatter: 'currency',
      currency: 'VND',
    },
    {
      label: 'o2o.create.summary.cost_total_amount',
      value: 0,
      formatter: 'currency',
      currency: 'VND',
    },
    {
      label: 'o2o.create.summary.total_discount',
      value: 0,
      formatter: 'currency',
      currency: 'VND',
    },
    {
      label: 'o2o.create.summary.pay_amount',
      value: 0,
      formatter: 'currency',
      currency: 'VND',
    },
  ],
};

// Form Fields
export const O2O_CREATE_FORM_FIELDS: FormField[] = [
  // Order Basic Information
  {
    label: 'o2o.create.form.third_party_order_number',
    controlName: 'thirdPartyOrderNumber',
    type: 'text',
    required: true,
    placeholder: 'o2o.create.form.placeholders.enter_order_number',
    section: 'o2o.create.form.sections.order_basic_information',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.third_party_order_number_required',
    },
    maxLength: 64,
  },
  {
    label: 'o2o.create.form.order_date',
    controlName: 'thirdPartyOrderTime',
    type: 'date',
    required: true,
    showTime: true,
    hourFormat: '24',
    minDate: new Date(2025, 1, 1),
    maxDate: null, // Will be set dynamically to current date in component
    placeholder: 'o2o.create.form.placeholders.select_date',
    section: 'o2o.create.form.sections.order_basic_information',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.order_date_required',
      maxDate: 'o2o.create.form.validation.order_date_future_not_allowed',
    },
  },
  {
    label: 'o2o.create.form.sales_channel',
    controlName: 'salesChannel',
    type: 'dropdown',
    required: true,
    placeholder: 'o2o.create.form.placeholders.select_channel',
    section: 'o2o.create.form.sections.order_basic_information',
    colspan: 1,
    defaultValue: 'website',
    options: [
      { label: 'o2o.create.form.options.official_website', value: 'website' },
    ],
    errorMessages: {
      required: 'o2o.create.form.validation.sales_channel_required',
    },
  },
  // {
  //   label: 'o2o.create.form.delivery_status',
  //   controlName: 'deliveryStatus',
  //   type: 'dropdown',
  //   placeholder: 'o2o.create.form.placeholders.select_delivery_status',
  //   options: ORDER_INTERNAL_DELIVER_STATUS_LIST,
  //   required: true,
  //   section: 'o2o.create.form.sections.order_basic_information',
  //   colspan: 1,
  //   errorMessages: {
  //     required: 'o2o.create.form.validation.delivery_status_required',
  //   },
  // },

  // Buyer Information
  {
    label: 'o2o.create.form.buyer_nickname',
    controlName: 'buyerNickname',
    type: 'text',
    required: true,
    placeholder: 'o2o.create.form.placeholders.enter_buyer_nickname',
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.buyer_nickname_required',
    },
    maxLength: 128,
  },
  {
    label: 'o2o.create.form.buyer_notes',
    controlName: 'buyerRemarks',
    type: 'text',
    placeholder: 'o2o.create.form.placeholders.buyer_notes',
    maxLength: 255,
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 1,
  },

  // Receiver Information
  {
    label: 'o2o.create.form.recipient_name',
    controlName: 'recipient',
    type: 'text',
    required: true,
    placeholder: 'o2o.create.form.placeholders.enter_recipient_name',
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.recipient_name_required',
    },
    maxLength: 128,
  },
  {
    label: 'o2o.create.form.recipient_phone',
    controlName: 'recipientPhone',
    type: 'text',
    required: true,
    validators: [vietnamesePhoneNumberValidator],
    placeholder: 'o2o.create.form.placeholders.enter_phone_number',
    maxLength: 20,
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.recipient_phone_required',
      invalidVietnamesePhone: 'common.validation.invalid_phone_format',
    },
  },
  // Province and Ward in Buyer & Receiver Information section
  {
    label: 'o2o.create.form.province',
    controlName: 'provinceId',
    type: 'searchableDropdown',
    placeholder: 'o2o.create.form.placeholders.select_province',
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 1,
    showClear: true,
    required: true, // Always required in buyer section
    options: [],
    errorMessages: {
      required: 'o2o.create.form.validation.province_required',
    },
  },
  {
    label: 'o2o.create.form.commune_ward',
    controlName: 'communeWardId',
    type: 'searchableDropdown',
    placeholder: 'o2o.create.form.placeholders.select_commune_ward',
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 1,
    showClear: true,
    required: true, // Always required in buyer section
    dependsOn: 'provinceId',
    options: [],
    errorMessages: {
      required: 'o2o.create.form.validation.commune_ward_required',
    },
  },
  // Fulfillment Method (new visual section)
  {
    label: 'o2o.create.form.taking_delivery_way',
    controlName: 'takingDeliveryWay',
    type: 'dropdown',
    placeholder: 'o2o.create.form.placeholders.select_delivery_way',
    options: O2O_CREATE_TAKING_DELIVERY_WAY_OPTIONS,
    required: true,
    section: 'o2o.create.form.sections.fulfillment_method',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.taking_delivery_way_required',
    },
  },
  {
    label: 'o2o.create.form.sales_store',
    controlName: 'salesStore',
    type: 'searchableDropdown',
    required: true,
    placeholder: 'o2o.create.form.placeholders.select_store',
    section: 'o2o.create.form.sections.fulfillment_method',
    colspan: 1,
    options: [], // Will be populated dynamically
    errorMessages: {
      required: 'o2o.create.form.validation.store_required',
    },
  },
  {
    label: 'o2o.create.form.shipping_address',
    controlName: 'shippingAddress',
    type: 'text',
    // Default takingDeliveryWay is STORE_PICKUP — required only toggled for SHIP_TO_ADDRESS in o2o-create
    required: false,
    placeholder: 'o2o.create.form.placeholders.enter_address',
    maxLength: 255,
    section: 'o2o.create.form.sections.buyer_recipient_information',
    colspan: 3,
    errorMessages: {
      required: 'o2o.create.form.validation.shipping_address_required',
    },
  },

  // Payment Information
  {
    label: 'o2o.create.form.payment_status',
    controlName: 'paymentStatus',
    type: 'dropdown',
    required: true,
    placeholder: 'o2o.create.form.placeholders.select_status',
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
    options: ORDER_INTERNAL_PAYMENT_STATUS_LIST,
    errorMessages: {
      required: 'o2o.create.form.validation.payment_status_required',
    },
  },
  {
    label: 'o2o.create.form.payment_method',
    controlName: 'paymentMethod',
    type: 'dropdown',
    required: true,
    placeholder: 'o2o.create.form.placeholders.select_method',
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
    options: ORDER_INTERNAL_PAYMENT_METHOD_LIST,
    errorMessages: {
      required: 'o2o.create.form.validation.payment_method_required',
    },
    hidden: true, // Hidden by default, shown when paymentStatus is Paid (1)
  },
  {
    label: 'o2o.create.form.payment_date',
    controlName: 'paymentTime',
    type: 'date',
    showTime: true,
    hourFormat: '24',
    minDate: new Date(2025, 1, 1), // Allow past dates for payment time
    maxDate: null, // Will be set dynamically to current date in component
    placeholder: 'o2o.create.form.placeholders.select_date',
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
    errorMessages: {
      maxDate: 'o2o.create.form.validation.payment_date_future_not_allowed',
    },
    hidden: true, // Hidden by default, shown when paymentStatus is Paid (1)
  },
  {
    label: 'o2o.create.form.transaction_id',
    controlName: 'transactionId',
    type: 'text',
    placeholder: 'o2o.create.form.placeholders.enter_transaction_id',
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
    maxLength: 255,
    hidden: true, // Hidden by default, shown when paymentStatus is Paid (1)
  },
  {
    label: 'o2o.create.form.goods_total_amount',
    controlName: 'goodsTotalAmount',
    type: 'currency',
    required: true,
    placeholder: '0.00',
    min: 0,
    readonly: true,
    disabled: true,
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
  },
  {
    label: 'o2o.create.form.cost_total_amount',
    controlName: 'costTotalAmount',
    type: 'currency',
    required: true,
    placeholder: '0.00',
    min: 0,
    readonly: true,
    disabled: true,
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
  },
  {
    label: 'o2o.create.form.shipping_amount',
    controlName: 'shippingAmount',
    type: 'currency',
    placeholder: '0.00',
    min: 0,
    readonly: true,
    disabled: true,
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
  },
  {
    label: 'o2o.create.form.payment_amount',
    controlName: 'paymentAmount',
    type: 'currency',
    required: true,
    placeholder: 'o2o.create.form.placeholders.enter_amount',
    min: 0,
    disabled: true,
    readonly: true,
    section: 'o2o.create.form.sections.payment_information',
    colspan: 1,
    errorMessages: {
      required: 'o2o.create.form.validation.payment_amount_required',
      min: 'o2o.create.form.validation.payment_amount_min',
    },
  },
  //   {
  //     label: 'o2o.create.form.includes_shipping_fee',
  //     controlName: 'includesShippingFee',
  //     type: 'radio',
  //     section: 'o2o.create.form.sections.payment_information',
  //     options: [
  //       { label: 'o2o.create.form.options.no', value: false },
  //       { label: 'o2o.create.form.options.yes', value: true }
  //     ]
  //   },

  // Discount Information
  //   {
  //     label: 'o2o.create.form.third_party_platform_discount',
  //     controlName: 'hasThirdPartyDiscount',
  //     type: 'radio',
  //     section: 'o2o.create.form.sections.discount_information',
  //     colspan: 2,
  //     options: [
  //       { label: 'o2o.create.form.options.no', value: false },
  //       { label: 'o2o.create.form.options.yes', value: true }
  //     ]
  //   },
  {
    label: 'o2o.create.form.available_promotions',
    controlName: 'selectedPromotionOffersDisplay',
    type: 'text',
    section: 'o2o.create.form.sections.discount_information',
    colspan: 2,
    placeholder: 'o2o.create.form.placeholders.select_promotions',
    readonly: false,
  },
  {
    label: 'o2o.create.form.coupon_discount',
    controlName: 'couponDiscount',
    type: 'couponAutocomplete',
    section: 'o2o.create.form.sections.discount_information',
    colspan: 2,
    placeholder: 'o2o.create.form.placeholders.enter_coupon_code',
    disabled: true, // Disabled by default, will be enabled when table has items
    readonly: true,
    maxLength: 100,
    errorMessages: {
      invalidCoupon: 'o2o.create.form.validation.invalid_coupon_code',
      duplicateCoupon: 'o2o.create.form.validation.coupon_already_exists',
    },
  },

  // Tracking Number - populated by the logistics provider once the order has shipped;
  // always read-only, never user-entered. Hidden on create (no shipment exists yet);
  // the view-mode form force-unhides all delivery_information fields to display it.
  {
    label: 'o2o.create.form.tracking_number',
    controlName: 'trackingNumber',
    type: 'text',
    section: 'o2o.create.form.sections.delivery_information',
    colspan: 1,
    readonly: true,
    disabled: true,
    hidden: true,
  },
];

// Main Create Configuration
export const O2O_CREATE_CONFIG: O2OCreateConfig = {
  headerActions: O2O_CREATE_HEADER_ACTIONS,
  tableSections: O2O_CREATE_TABLE_SECTIONS,
  summarySection: O2O_CREATE_SUMMARY_SECTION,
  formFields: O2O_CREATE_FORM_FIELDS,
};

// ============================================================================
// CONFIGURATION FUNCTIONS
// ============================================================================

/**
 * Get O2O Internal configuration with dynamic options
 */
export function getO2OInternalConfig(options?: {
  salesChannels?: Array<{ label: string; value: string }>;
  salesStores?: Array<{ label: string; value: string }>;
  orderSources?: Array<{ label: string; value: string }>;
  pickupStores?: Array<{ label: string; value: string }>;
  pickupMethods?: Array<{ label: string; value: string }>;
  receiptStatuses?: Array<{ label: string; value: string }>;
}): O2OInternalConfig {
  const config = { ...O2O_INTERNAL_CONFIG };

  if (options) {
    // Update search config with dynamic options
    config.searchConfig = config.searchConfig.map((field) => {
      switch (field.key) {
        case 'salesChannel':
          return { ...field, options: options.salesChannels || [] };
        case 'salesStore':
          return { ...field, options: options.salesStores || [] };
        case 'orderSource':
          return { ...field, options: options.orderSources || [] };
        case 'pickupStore':
          return { ...field, options: options.pickupStores || [] };
        case 'pickupMethod':
          return { ...field, options: options.pickupMethods || [] };
        case 'receiptStatus':
          return { ...field, options: options.receiptStatuses || [] };
        default:
          return field;
      }
    });
  }

  return config;
}

/**
 * Get O2O Create configuration with dynamic data
 */
export function getO2OCreateConfig(data?: {
  products?: OrderProduct[];
  selectedProducts?: OrderProduct[];
  provinceOptions?: Array<{ label: string; value: number }>;
}): O2OCreateConfig {
  const config = { ...O2O_CREATE_CONFIG };

  if (data) {
    // Update table sections with dynamic data
    config.tableSections = config.tableSections.map((section) => ({
      ...section,
      data: data.products || [],
      selection: data.selectedProducts || [],
    }));

    // Update form fields with dynamic options
    config.formFields = config.formFields.map((field) => {
      if (field.controlName === 'provinceId' && data.provinceOptions) {
        return { ...field, options: data.provinceOptions };
      }
      return field;
    });
  }

  return config;
}

/**
 * Create a new empty product
 */
export function createEmptyProduct(serialNo: number): OrderProduct {
  return {
    id: Date.now(),
    serialNo,
    productName: '',
    productCode: '',
    skuName: '',
    skuCode: '',
    quantity: 1,
    isGift: false,
    thirdPartyPrice: 0,
    thirdPartyTotal: 0,
    internalPrice: 0,
    internalTotal: 0,
    costPrice: 0,
    costTotal: 0,
  };
}
